import { createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type PropsWithChildren } from 'react';
import { useAuth } from '../auth/AuthContext';
import { ActiveDatabaseManager, databaseManager } from './databaseManager';
import { LEGACY_DATABASE_NAME } from './db';

interface ActiveDatabaseContextValue {
  databaseName: string;
  revision: number;
}

const ActiveDatabaseContext = createContext<ActiveDatabaseContextValue>({ databaseName: LEGACY_DATABASE_NAME, revision: 0 });

const DatabaseTransition = ({ error, retry }: { error?: string; retry?: () => void }) => (
  <div className="page loading-page" {...(error ? { role: 'alert' } : { role: 'status' })}>
    {!error && <span className="loading-orb" />}
    <span>{error ?? 'Abrindo sua biblioteca local…'}</span>
    {retry && <button type="button" className="auth-link" onClick={retry}>Tentar novamente</button>}
  </div>
);

export const ActiveDatabaseProvider = ({
  children,
  manager = databaseManager
}: PropsWithChildren<{ manager?: ActiveDatabaseManager }>) => {
  const auth = useAuth();
  const snapshot = useSyncExternalStore(manager.subscribe, manager.getSnapshot, manager.getSnapshot);
  const [switchFailure, setSwitchFailure] = useState<{ databaseName: string; message: string }>();
  const [retryKey, setRetryKey] = useState(0);
  const userId = auth.user?.id ?? null;

  let desiredDatabaseName: string | undefined;
  let identityError: string | undefined;
  try {
    desiredDatabaseName = manager.getDatabaseNameForUser(userId);
  } catch (error) {
    identityError = error instanceof Error ? error.message : 'A identidade da conta é inválida.';
  }

  useEffect(() => {
    if (auth.isLoading || identityError || !desiredDatabaseName || snapshot.databaseName === desiredDatabaseName) return;
    let active = true;
    void manager.switchToUser(userId).catch((error: unknown) => {
      if (active) setSwitchFailure({
        databaseName: desiredDatabaseName,
        message: error instanceof Error ? error.message : 'Não foi possível trocar a biblioteca local.'
      });
    });
    return () => { active = false; };
  }, [auth.isLoading, desiredDatabaseName, identityError, manager, retryKey, snapshot.databaseName, userId]);

  const value = useMemo<ActiveDatabaseContextValue>(() => ({
    databaseName: snapshot.databaseName,
    revision: snapshot.revision
  }), [snapshot.databaseName, snapshot.revision]);

  if (auth.isLoading) return <DatabaseTransition />;
  if (identityError) return <DatabaseTransition error={identityError} />;
  if (snapshot.databaseName !== desiredDatabaseName) {
    const switchError = switchFailure && switchFailure.databaseName === desiredDatabaseName
      ? switchFailure.message
      : undefined;
    return switchError
      ? <DatabaseTransition error={switchError} retry={() => {
        setSwitchFailure(undefined);
        setRetryKey((value) => value + 1);
      }} />
      : <DatabaseTransition />;
  }

  return <ActiveDatabaseContext.Provider value={value}>{children}</ActiveDatabaseContext.Provider>;
};

// Tests de componentes isolados permanecem no banco legado pelo valor padrão.
// eslint-disable-next-line react-refresh/only-export-components
export const useActiveDatabaseRevision = (): number => useContext(ActiveDatabaseContext).revision;
